import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GolfTours } from './GolfTours';
import { FeaturedProductsStrip } from './FeaturedProductsStrip';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LOGO_URL =
  '/api/static/brand/logo-h0.png';

const ARAVELL_BALL_URL =
  '/api/static/brand/ugolf-aravell-blanco.svg';

const ARAVELL_VISIT = {
  ca: "Visita'ns!",
  es: '¡Ven!',
  en: 'Visit us!',
  fr: 'Viens nous voir !',
};

const LANGS = [
  { code: 'ca', label: 'CA' },
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];

const Door = ({ index, text, side, onClick, testid }) => {
  const isLeft = side === 'left';
  return (
    <motion.button
      type="button"
      onClick={onClick}
      data-testid={testid}
      initial={{ opacity: 0, x: isLeft ? -40 : 40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.8, delay: 0.6 + index * 0.15, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative w-full md:w-1/2 flex ${
        isLeft ? 'md:justify-end md:pr-10 lg:pr-16' : 'md:justify-start md:pl-10 lg:pl-16'
      } justify-center py-3`}
    >
      {/* Connector line */}
      <span
        className={`hidden md:block absolute top-1/2 ${
          isLeft ? 'right-0' : 'left-0'
        } w-8 lg:w-12 h-px bg-[#D4AF37]/30 group-hover:bg-[#D4AF37] group-hover:w-16 lg:group-hover:w-20 transition-all duration-500`}
      />
      {/* Dot on timeline */}
      <span
        className={`hidden md:block absolute top-1/2 -translate-y-1/2 ${
          isLeft ? 'right-0 translate-x-1/2' : 'left-0 -translate-x-1/2'
        } w-2 h-2 rounded-full bg-[#D4AF37]/40 group-hover:bg-[#D4AF37] group-hover:scale-150 transition-all duration-500`}
      />
      <span
        className={`relative font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl leading-tight text-[#F2E8D5] group-hover:text-[#E5C158] transition-all duration-500 ${
          isLeft ? 'md:text-right' : 'md:text-left'
        } text-center max-w-md tracking-tight`}
      >
        {text}
        <span className="block h-px w-0 group-hover:w-full bg-gradient-to-r from-[#D4AF37]/0 via-[#D4AF37] to-[#D4AF37]/0 transition-all duration-700 mt-1" />
      </span>
    </motion.button>
  );
};

export const H0Portal = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoEmbed, setVideoEmbed] = useState('');
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    setMounted(true);
    axios.get(`${API_URL}/api/home-video`).then((r) => {
      setVideoUrl(r.data?.url || '');
      setVideoEmbed(r.data?.embed_url || '');
    }).catch(() => {});
  }, []);

  const handleSIG = () => {
    // SIG is a proper funnel: open it in a new tab so the H0 home stays here as
    // a "back" option. Direct visitors of /sig (paid ads etc) only have 2 exits:
    // close the tab or the WhatsApp CTA — no nav escape inside the page.
    window.open('/sig', '_blank', 'noopener');
  };

  const handleLang = (code) => {
    i18n.changeLanguage(code);
  };

  const doors = [
    {
      key: 'sig',
      text: t('h0.doors.sig'),
      side: 'right',
      onClick: handleSIG,
      testid: 'door-sig',
    },
    {
      key: 'nuts',
      text: t('h0.doors.nuts'),
      side: 'left',
      onClick: () => navigate('/nuts'),
      testid: 'door-nuts',
    },
    {
      key: 'caps',
      text: t('h0.doors.caps'),
      side: 'right',
      onClick: () => navigate('/caps'),
      testid: 'door-caps',
    },
    {
      key: 'shark',
      text: t('h0.doors.shark', { defaultValue: 'Shark' }),
      side: 'left',
      onClick: () => navigate('/shark'),
      testid: 'door-shark',
    },
    {
      key: 'quinta',
      text: t('h0.doors.quinta', { defaultValue: 'Ets de la Quinta?' }),
      side: 'right',
      onClick: () => navigate('/quinta'),
      testid: 'door-quinta',
    },
  ];

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-[#0E4D2A] text-[#F2E8D5]"
      data-testid="h0-portal"
    >
      {/* Watermark giant H0 */}
      <div className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center select-none">
        <span
          className="font-serif font-black text-[#083B1F]"
          style={{ fontSize: 'min(85vw, 85vh)', lineHeight: 1, opacity: 0.55 }}
          aria-hidden="true"
        >
          H0
        </span>
      </div>

      {/* Grain / noise overlay */}
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Vignette */}
      <div className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_rgba(0,0,0,0.55)_100%)]" />

      {/* Sponsor / partner: UGOLF Aravell logo — top-left, mirrors the
          language switcher on the top-right. Click → opens aravellgolfclub.com
          in a new tab. */}
      <motion.a
        href="https://aravellgolfclub.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="aravell-ball-link"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.06, transition: { duration: 0.3 } }}
        className="fixed top-5 left-5 md:top-7 md:left-8 z-30 flex flex-col items-start group"
      >
        <img
          src={ARAVELL_BALL_URL}
          alt="UGOLF Aravell Andorra"
          className="h-10 md:h-12 w-auto opacity-90 group-hover:opacity-100 drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)] transition-opacity"
        />
        <span className="mt-1.5 font-sans tracking-[0.28em] uppercase text-[9px] md:text-[10px] text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          {ARAVELL_VISIT[(i18n.language || 'ca').split('-')[0]] || ARAVELL_VISIT.ca}
        </span>
      </motion.a>

      {/* Language switcher + Video button */}
      <div
        className="fixed top-6 right-6 md:top-8 md:right-10 z-30 flex items-center gap-4 md:gap-5 font-sans text-[11px] md:text-xs tracking-[0.25em]"
        data-testid="top-right-controls"
      >
        {videoUrl && (
          <button
            type="button"
            onClick={() => setShowVideo(true)}
            data-testid="home-video-btn"
            className="group inline-flex items-center gap-1.5 text-[#D4AF37] hover:text-[#E5C158] uppercase transition-colors duration-300"
            aria-label={t('h0.videoLabel', { defaultValue: 'Video' })}
          >
            <span className="inline-flex items-center justify-center w-5 h-5 md:w-5 md:h-5 rounded-full border border-[#D4AF37]/70 group-hover:border-[#D4AF37] transition-colors">
              <span className="ml-0.5 border-y-[4px] border-y-transparent border-l-[6px] border-l-[#D4AF37] group-hover:border-l-[#E5C158]" />
            </span>
            <span>{t('h0.videoLabel', { defaultValue: 'Video' })}</span>
          </button>
        )}
        <div className="flex gap-3 md:gap-4" data-testid="language-switcher">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => handleLang(l.code)}
              data-testid={`lang-${l.code}`}
              className={`transition-colors duration-300 ${
                i18n.language === l.code
                  ? 'text-[#D4AF37]'
                  : 'text-[#A8B4A6] hover:text-[#F2E8D5]'
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-start min-h-screen px-4 py-10 md:py-14">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={mounted ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center"
          data-testid="h0-logo"
        >
          <img
            src={LOGO_URL}
            alt="Hoyo0 H0"
            className="w-36 sm:w-44 md:w-52 lg:w-60 h-auto drop-shadow-[0_8px_30px_rgba(212,175,55,0.15)]"
          />
        </motion.div>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={mounted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.4 }}
          className="mt-6 md:mt-8 text-center text-[#D4AF37] font-sans font-light tracking-[0.32em] uppercase text-[10px] sm:text-xs md:text-[13px]"
          data-testid="h0-tagline"
        >
          {t('h0.tagline')}
        </motion.p>

        {/* Brand name (text adapted per language) */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={mounted ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 0.55 }}
          className="mt-2 text-center font-serif italic text-[#A8B4A6] text-sm sm:text-base tracking-wide"
          data-testid="h0-brandname"
        >
          {t('h0.brandName')}
        </motion.p>

        {/* Doors timeline */}
        <div className="relative w-full max-w-5xl mt-10 md:mt-14 flex flex-col items-center">
          {/* Central vertical line (desktop) */}
          <div className="hidden md:block absolute top-2 bottom-16 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-[#D4AF37]/0 via-[#D4AF37]/30 to-[#D4AF37]/0" />

          {doors.map((d, idx) => (
            <Door
              key={d.key}
              index={idx}
              text={d.text}
              side={d.side}
              onClick={d.onClick}
              testid={d.testid}
            />
          ))}

          {/* Amazon Affiliate Storefront — "De compres pel Món" (before Golf Tours) */}
          <motion.button
            type="button"
            onClick={() => navigate('/botiga')}
            data-testid="door-botiga"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: 1.03 }}
            className="mt-14 md:mt-16 group relative flex flex-col items-center font-sans"
          >
            <span className="inline-flex items-center gap-2 border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#F2E8D5] hover:text-[#D4AF37] rounded-full px-6 py-2.5 text-xs md:text-sm tracking-[0.25em] uppercase transition-all duration-500 bg-[#083B1F]/40 hover:bg-[#083B1F]/70">
              🛒 {t('h0.shopLabel')}
            </span>
            <span className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[#A8B4A6]/70">
              {t('h0.shopHint')}
            </span>
          </motion.button>

          {/* Featured products strip (only renders if any ⭐ featured exists) */}
          <div className="w-full">
            <FeaturedProductsStrip />
          </div>

          {/* Golf Tours — live leaderboards (before ON-IA-GOLF, inline) */}
          <div className="w-full mt-16">
            <GolfTours />
          </div>

          {/* Door 5 — ON-IA-GOLF (secondary access) */}
          <motion.button
            type="button"
            onClick={() => navigate('/on-ia-golf')}
            data-testid="door-on-ia-golf"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 md:mt-16 group relative font-sans tracking-[0.42em] text-[10px] md:text-xs text-[#A8B4A6] hover:text-[#D4AF37] border border-[#A8B4A6]/30 hover:border-[#D4AF37] rounded-full px-7 py-2.5 transition-all duration-500"
          >
            ON-IA-GOLF
            <span className="absolute inset-0 rounded-full bg-[#D4AF37]/0 group-hover:bg-[#D4AF37]/5 transition-all duration-500" />
          </motion.button>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.6 }}
            className="mt-3 text-[10px] tracking-[0.25em] uppercase text-[#A8B4A6]/60 font-sans"
          >
            {t('h0.onIaGolfHint')}
          </motion.p>

          {/* ZONA PRO — admin entry (subtle, gold-tinted) */}
          <motion.button
            type="button"
            onClick={() => navigate('/zona-pro/login')}
            data-testid="door-zona-pro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.9 }}
            className="mt-6 font-sans tracking-[0.42em] text-[9px] md:text-[10px] text-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors duration-500 uppercase"
          >
            ZONA PRO
          </motion.button>
        </div>

        {/* Footer signature */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={mounted ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1.8 }}
          className="mt-auto pt-12 pb-6 text-center text-[10px] tracking-[0.3em] text-[#A8B4A6]/50 font-sans uppercase"
        >
          © {new Date().getFullYear()} · @joan_monzo
        </motion.div>
      </div>

      {/* Presentation video modal */}
      <AnimatePresence>
        {showVideo && videoEmbed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={() => setShowVideo(false)}
            data-testid="home-video-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="relative w-full max-w-5xl aspect-video bg-black rounded-xl overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setShowVideo(false)}
                aria-label="Close"
                data-testid="home-video-close"
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white text-xl leading-none flex items-center justify-center transition-colors"
              >
                ×
              </button>
              <iframe
                title="Presentation video"
                src={videoEmbed}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default H0Portal;
